import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/login.css';
import { getBranding } from '../config/branding';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await login(email, password, rememberMe, requires2FA ? totpCode : undefined);

      if (result.success) {
        navigate('/', { replace: true });
      } else if (result.requires_2fa) {
        setRequires2FA(true);
        setError('');
      } else {
        setError(result.error || t('login.failed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="login-page">
        <div className="login-loading">
          <div className="login-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {/* Animated background */}
      <div className="login-bg">
        <div className="login-bg-gradient" />
        <div className="login-bg-noise" />
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      <div className="login-container">
        {/* Left side - branding */}
        <div className="login-branding">
          <div className="login-branding-content">
            {getBranding().edition !== 'sense'
              ? <span className="brand-mark login-brand-mark">{getBranding().brand_name.toLowerCase()}</span>
              : <img src="/logo.png" alt={getBranding().brand_name} className="login-logo" />}
            <h1 className="login-tagline">
              {t('login.taglineBefore')}<br />
              <span>{t('login.taglineAccent')}</span><br />
              {t('login.taglineAfter')}
            </h1>
            <p className="login-description">
              {t('login.description')}
            </p>
          </div>
          <div className="login-branding-footer">
            <div className="login-feature">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>{t('login.security')}</span>
            </div>
            <div className="login-feature">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              <span>{t('login.analytics')}</span>
            </div>
          </div>
        </div>

        {/* Right side - form */}
        <div className="login-form-container">
          <div className="login-form-card">
            <div className="login-form-header">
              <h2>{requires2FA ? t('login.twoFactor') : t('login.signIn')}</h2>
              <p>
                {requires2FA
                  ? t('login.codeHint')
                  : t('login.credsHint')}
              </p>
            </div>

            {new URLSearchParams(location.search).get('verified') === '1' && (
              <div className="login-notice">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span>Email verified — your account is pending admin approval. You'll be able to sign in once an admin approves it.</span>
              </div>
            )}
            {new URLSearchParams(location.search).get('verify_error') === '1' && (
              <div className="login-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{t('login.linkInvalid')}</span>
              </div>
            )}

            {error && (
              <div className="login-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              {!requires2FA ? (
                <>
                  <div className="login-field">
                    <label htmlFor="email">{t('login.title')}</label>
                    <div className="login-input-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <input
                        id="email"
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('login.loginPlaceholder')}
                        required
                        autoComplete="username"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="login-field">
                    <label htmlFor="password">{t('login.password')}</label>
                    <div className="login-input-wrapper">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('login.passwordPlaceholder')}
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="login-options">
                    <label className="login-checkbox">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span className="login-checkbox-mark" />
                      <span>{t('login.remember')}</span>
                    </label>
                  </div>
                </>
              ) : (
                <div className="login-field">
                  <label htmlFor="totp">{t('login.code')}</label>
                  <div className="login-input-wrapper login-input-totp">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      <circle cx="12" cy="16" r="1"/>
                    </svg>
                    <input
                      id="totp"
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      required
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={6}
                      pattern="[0-9]{6}"
                    />
                  </div>
                  <button
                    type="button"
                    className="login-back-link"
                    onClick={() => {
                      setRequires2FA(false);
                      setTotpCode('');
                      setError('');
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="19" y1="12" x2="5" y2="12"/>
                      <polyline points="12 19 5 12 12 5"/>
                    </svg>
                    {t('login.backToLogin')}
                  </button>
                </div>
              )}

              <button
                type="submit"
                className="login-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="login-submit-spinner" />
                ) : requires2FA ? (
                  t('login.verify')
                ) : (
                  t('login.signIn')
                )}
              </button>
              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary, #6c757d)', margin: '8px 0 0' }}>
                Don't have an account?{' '}
                <Link to="/register" style={{ color: '#FF6B4A', fontWeight: 600, textDecoration: 'none' }}>
                  {t('login.createAccount')}
                </Link>
              </p>
            </form>
          </div>

          <p className="login-footer-text">
            &copy; {new Date().getFullYear()} {getBranding().brand_name} Platform. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
